/** Shapes returned by the six back-office modules StaffDesk fronts (fields the portal uses). */

// ─── C13 StaffDesk (port 4115, gateway key `staffdesk`) ─────────────────────

export interface Officer {
  id: string;
  personId: string;
  mdaCode: string;
  position: string;
  roles: string[];
  status: 'ACTIVE' | 'SUSPENDED' | 'LEFT';
  /** Own roles ∪ active-delegation roles — only present on GET /officers/:id. */
  effectiveRoles?: string[];
  createdAt: string;
}

export interface Delegation {
  id: string;
  fromOfficerId: string;
  toOfficerId: string;
  roles: string[];
  from: string;
  to: string;
  reason: 'LEAVE' | 'ACTING';
  endedAt?: string | null;
  endedBy?: string | null;
  createdAt: string;
}

export interface TaskInboxItem {
  id: string;
  sourceModule: string;
  sourceRef: string;
  officerId: string;
  title: string;
  dueAt: string | null;
  priority: number;
  status: 'OPEN' | 'DONE';
  doneAt?: string | null;
  doneBy?: string | null;
  createdAt: string;
}

export interface RoleGrant {
  id: string;
  officerId: string;
  role: string;
  action: 'GRANT' | 'REVOKE';
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  requestedBy: string;
  approvedBy?: string | null;
  rejectedReason?: string | null;
  createdAt: string;
}

export interface OrgChartResponse {
  mdaCode: string;
  positions: Array<{
    position: string;
    officers: Array<{ id: string; personId: string; roles: string[] }>;
  }>;
}

// ─── C5 FlowEngine (port 4105, gateway key `flow`) ──────────────────────────

export interface FlowCase {
  id: string;
  refNo: string;
  flowCode: string;
  flowVersion: number;
  applicantType: 'PERSON' | 'ENTITY';
  applicantId: string;
  subjectJson: unknown;
  state: string;
  stateRole?: string | null;
  terminal: boolean;
  assigneeId?: string | null;
  priority: number;
  slaStartedAt?: string | null;
  slaDueAt?: string | null;
  escalationLevel: number;
  feeStatus: 'NONE' | 'PENDING' | 'PAID';
  feeIntentRef?: string | null;
  createdAt: string;
}

export interface CaseEvent {
  id: string;
  caseId: string;
  type:
    | 'TRANSITION'
    | 'COMMENT'
    | 'DOC_ADDED'
    | 'REFERRAL_SENT'
    | 'DECISION'
    | 'ESCALATION'
    | 'ASSIGNMENT'
    | 'CHECKLIST'
    | 'FEE'
    | 'APPEAL';
  fromState?: string | null;
  toState?: string | null;
  actorId: string;
  payload?: unknown;
  createdAt: string;
}

export interface ChecklistItem {
  id: string;
  caseId: string;
  code: string;
  label: string;
  status: 'PENDING' | 'MET' | 'WAIVED';
  evidenceDocId?: string | null;
  verifiedBy?: string | null;
}

export interface Referral {
  id: string;
  caseId: string;
  targetMda: string;
  service: string;
  status: 'SENT' | 'RESPONDED' | 'OVERDUE';
  slaDueAt?: string | null;
  respondedBy?: string | null;
  respondedAt?: string | null;
  createdAt: string;
}

export interface Decision {
  id: string;
  caseId: string;
  outcome: string;
  templateCode?: string | null;
  conditions: string[];
  signedBy: string;
  noticeDocId?: string | null;
  createdAt: string;
}

export interface Appeal {
  id: string;
  caseId: string;
  grounds: string;
  state: 'OPEN' | 'BEFORE_TRIBUNAL' | 'UPHELD' | 'OVERTURNED' | 'WITHDRAWN';
  tribunalRef?: string | null;
  createdAt: string;
}

export interface CaseDetail extends FlowCase {
  timeline: CaseEvent[];
  checklist: ChecklistItem[];
  referrals: Referral[];
  decisions: Decision[];
  appeals: Appeal[];
  availableActions: Array<{ action: string; to: string; roles: string[] }>;
}

export interface FlowDefinition {
  id: string;
  code: string;
  moduleCode: string;
  version: number;
  /** The FlowDsl blob — {refPrefix, initial, states: {...}}; rendered generically, see DefinitionDetail.tsx. */
  dsl: unknown;
  active: boolean;
  createdAt: string;
}

// ─── C4 SeyNotify (port 4104, gateway key `notify`) ─────────────────────────

export interface Template {
  id: string;
  code: string;
  moduleCode: string;
  channel: 'WHATSAPP' | 'SMS' | 'EMAIL' | 'PUSH';
  lang: 'en' | 'fr' | 'crs';
  body: string;
  waTemplateName?: string | null;
  approvedAt?: string | null;
  createdAt: string;
}

export interface Broadcast {
  id: string;
  templateCode: string;
  audienceQuery: { personIds: string[] };
  scheduledAt?: string | null;
  priority: boolean;
  status: 'DRAFT' | 'SCHEDULED' | 'DISPATCHED';
  stats: { audience?: number; sent?: number; queued?: number; failed?: number };
  dispatchedAt?: string | null;
  createdAt: string;
}

export interface PostalTask {
  id: string;
  messageId: string;
  recipientId: string;
  caseRef?: string | null;
  reason: string;
  status: 'PENDING' | 'PRINTED' | 'DISPATCHED' | 'COMPLETED';
  proofDocId?: string | null;
  createdAt: string;
  completedAt?: string | null;
}

// ─── C7 ConsentHub (port 4107, gateway key `consenthub`) ────────────────────

export interface LegalBasis {
  id: string;
  seyxService: string;
  consumerMda: string;
  statuteRef: string;
  approvedBy: string;
  reviewDate: string;
  retiredAt?: string | null;
  retiredReason?: string | null;
  createdAt: string;
}

// ─── C3 DocVault (port 4103, gateway key `docs`) ────────────────────────────

export interface DocTypeField {
  key: string;
  label: string;
  type: 'string' | 'number' | 'date';
  required: boolean;
}

export interface DocType {
  id: string;
  code: string;
  name: string;
  issuerMda: string;
  schemaJson: { fields: DocTypeField[] };
  validityDays?: number | null;
  revocable: boolean;
  createdAt: string;
}

/** Response of the public GET /api/v1/docs/verify/:qrToken surface. */
export interface VerifyResult {
  valid: boolean;
  status?: 'VALID' | 'REVOKED' | 'EXPIRED' | 'SUPERSEDED';
  serial?: string;
  docTypeCode?: string;
  issuedAt?: string;
  expiresAt?: string;
  statusReason?: string;
}

// ─── C9 GeoServe (port 4109, gateway key `geoserve`) ────────────────────────

export const ISLANDS = ['MAHE', 'PRASLIN', 'LA_DIGUE', 'SILHOUETTE', 'OTHER'] as const;

/** Real Seychelles administrative districts — mirrors c9-geoserve's own SEYCHELLES_DISTRICTS list (src/service/districts.ts). */
export const SEYCHELLES_DISTRICTS = [
  'Beau Vallon',
  'Anse Royale',
  'Baie Lazare',
  'Anse Boileau',
  'Takamaka',
  'Port Glaud',
  'Cascade',
  'Au Cap',
  'Anse Aux Pins',
  'English River',
  'Mont Buxton',
  'Plaisance',
  'St Louis',
  'Roche Caiman',
  'Bel Air',
  'Mont Fleuri',
  'Glacis',
  'Bel Ombre',
  'Anse Etoile',
  'Machabee',
  'Pointe Larue',
  'Grand Anse Mahe',
  'Grand Anse Praslin',
  'Baie Ste Anne',
] as const;

export interface Address {
  id: string;
  parcelNo?: string | null;
  street: string;
  locality: string;
  district: string;
  island: (typeof ISLANDS)[number];
  geometry: unknown;
  status: 'DRAFT' | 'ASSIGNED' | 'RETIRED';
  createdAt: string;
}
